import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { editorAccent } from '../../../editor-accent';
import InspectorRow from './inspector-row';
import InspectorSection from './inspector-section';
import NumberField from './number-field';
import { CORNERS, type CornerRadiiControl } from './use-corner-radii';

const MIXED = '(разное)';

interface CornerRadiusSectionProps {
  control: CornerRadiiControl;
}

/**
 * Shared "Corner Radius" inspector section: a collapsed uniform Radius field with
 * a header toggle that expands to per-corner TL/TR/BR/BL fields. Auto-expands when
 * the selection already carries explicit per-corner radii so the visual and the
 * inspector never disagree. Kind-agnostic — both the image-placeholder and the
 * native-rect inspectors drive it through a `CornerRadiiControl` (see
 * `useCornerRadii` + the corner-radii strategies).
 */
const CornerRadiusSection: React.FC<CornerRadiusSectionProps> = ({ control }) => {
  const [expanded, setExpanded] = useState(control.hasPerCorner);
  useEffect(() => setExpanded(control.hasPerCorner), [control.hasPerCorner]);

  return (
    <InspectorSection
      title="Скругление"
      headerAction={
        <ExpandToggle type="button" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
          {expanded ? '▴ Равномерно' : '▾ По углам'}
        </ExpandToggle>
      }
    >
      <InspectorRow label="Радиус">
        <NumberField
          value={control.uniform.isMixed ? undefined : control.uniform.value}
          onChange={control.setUniform}
          min={0}
          emptyValue={0}
          placeholder={control.uniform.isMixed ? MIXED : undefined}
        />
      </InspectorRow>
      {expanded &&
        CORNERS.map(({ key, label }) => (
          <InspectorRow key={key} label={label}>
            <NumberField
              value={control.corners[key].isMixed ? undefined : control.corners[key].value}
              onChange={(v) => control.setCorner(key, v)}
              min={0}
              emptyValue={0}
              placeholder={control.corners[key].isMixed ? MIXED : undefined}
            />
          </InspectorRow>
        ))}
    </InspectorSection>
  );
};

export default CornerRadiusSection;

const ExpandToggle = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  font-size: 11px;
  font-weight: 600;
  color: ${editorAccent.ink};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: ${editorAccent.inkHover};
  }
`;
